const rotatePoint = (x, y, degrees) => {
  const radians = -degrees * Math.PI / 180;
  return {
    x: x * Math.cos(radians) - y * Math.sin(radians),
    y: x * Math.sin(radians) + y * Math.cos(radians)
  };
};

export const FLAG_ZONE_ORDER = ['RED', 'YELLOW', 'GREEN'];

export const DEFAULT_FLAG_KEY_BINDINGS = {
  RED: 'Numpad1',
  YELLOW: 'Numpad2',
  GREEN: 'Numpad3'
};

const normalizePhysicalKeyCode = (value) => {
  const code = typeof value === 'string' ? value.trim() : '';
  const numpadMatch = code.match(/^(?:NUM(?:PAD)?\s*)?([0-9])$/i);
  if (numpadMatch) return `Numpad${numpadMatch[1]}`;
  if (/^Numpad[0-9]$/i.test(code)) return `Numpad${code.slice(-1)}`;
  if (/^Digit[0-9]$/i.test(code)) return `Digit${code.slice(-1)}`;
  if (/^Key[A-Z]$/i.test(code)) return `Key${code.slice(-1).toUpperCase()}`;
  if (/^[A-Z]$/i.test(code)) return `Key${code.toUpperCase()}`;
  return code;
};

export const normalizeFlagKeyBindings = (bindings = {}, defaults = DEFAULT_FLAG_KEY_BINDINGS) => {
  const normalized = {};
  const usedCodes = new Set();

  FLAG_ZONE_ORDER.forEach((zone) => {
    const storedCode = normalizePhysicalKeyCode(bindings?.[zone]);
    const availableDefault = FLAG_ZONE_ORDER
      .map((candidate) => defaults[candidate])
      .find((candidate) => !usedCodes.has(candidate));
    const code = storedCode && !usedCodes.has(storedCode)
      ? storedCode
      : availableDefault;
    normalized[zone] = code;
    usedCodes.add(code);
  });

  return normalized;
};

export const getFlagZoneForCode = (code, bindings = DEFAULT_FLAG_KEY_BINDINGS) => (
  FLAG_ZONE_ORDER.find((zone) => bindings?.[zone] === code) || null
);

export const formatFlagKeyCode = (code) => {
  if (!code) return 'Not bound';
  if (/^Numpad\d$/.test(code)) return `NUM ${code.slice(-1)}`;
  if (/^Digit\d$/.test(code)) return code.slice(-1);
  if (/^Key[A-Z]$/.test(code)) return code.slice(-1);
  const labels = {
    Space: 'SPACE',
    Enter: 'ENTER',
    NumpadEnter: 'NUM ENTER',
    ArrowUp: 'UP',
    ArrowDown: 'DOWN',
    ArrowLeft: 'LEFT',
    ArrowRight: 'RIGHT'
  };
  return labels[code] || code.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
};

const triangleContains = ({ x, y }) => {
  const first = { x: 0, y: -52 };
  const second = { x: -44, y: 36 };
  const third = { x: 44, y: 36 };
  const sign = (point, edgeStart, edgeEnd) => (
    (point.x - edgeEnd.x) * (edgeStart.y - edgeEnd.y)
    - (edgeStart.x - edgeEnd.x) * (point.y - edgeEnd.y)
  );
  const d1 = sign({ x, y }, first, second);
  const d2 = sign({ x, y }, second, third);
  const d3 = sign({ x, y }, third, first);
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNegative && hasPositive);
};

export const flagZoneContains = (zone, plane) => {
  const local = rotatePoint(plane.x - zone.x, plane.y - zone.y, zone.rotation || 0);
  if (zone.shape === 'ellipse') return (local.x ** 2) / (40 ** 2) + (local.y ** 2) / (48 ** 2) <= 1;
  if (zone.shape === 'rect') return Math.abs(local.x) <= 36 && Math.abs(local.y) <= 36;
  return triangleContains(local);
};

export const getFlagPaceMultiplier = (progress, difficulty = 'medium') => {
  const bounded = Math.max(0, Math.min(1, Number(progress) || 0));
  const growth = difficulty === 'hard' ? 1.05 : difficulty === 'easy' ? 0.62 : 0.82;
  // Ease into the speed increase so the opening phase is deliberately calm.
  const eased = bounded ** 1.6;
  return 1 + eased * growth;
};

export const getFlagAircraftTargetCount = (progress, difficulty = 'medium', assessment = false) => {
  const bounded = Math.max(0, Math.min(1, Number(progress) || 0));
  if (assessment) {
    if (bounded < 0.12) return 2;
    if (bounded < 0.28) return 3;
    if (bounded < 0.46) return 4;
    if (bounded < 0.64) return 5;
    if (bounded < 0.8) return 6;
    if (bounded < 0.92) return 7;
    return 8;
  }
  const base = difficulty === 'hard' ? 4 : difficulty === 'easy' ? 2 : 3;
  const maximum = difficulty === 'hard' ? 8 : difficulty === 'easy' ? 4 : 6;
  return base + Math.min(maximum - base, Math.floor(bounded * (maximum - base + 1)));
};

export const getTrackedCallsigns = (planes, activeOnly = false) => planes
  .filter((plane) => plane.monitored && (!activeOnly || plane.active))
  .map((plane) => plane.callsign);
